// React
import {
	ReactElement,
	useCallback,
	useMemo,
	useState,
	useEffect,
	useContext,
} from "react"

// React Query
import { QueryClient } from "@/lib/query"

// Hooks
import { useOnInactivity } from "@/hooks/useOnInactivity"

// Constants
import { NetworkError } from "@/api/rest/network-errors"

// i18n
import { useTrans } from "@/i18n"

// Toast
import { useToasts } from "./useToasts"

// Context
import { useCurrentUserId } from "./user"
import { RedirectContext } from "./redirectContext"

import { persistor } from "@/state/store"

// Environment variables
import { AUTH_LOGOUT_URL } from "@/lib/env"

// Auth hook and types
import { AuthContext, AuthStateStatusEnum, AuthContextType } from "./useAuth"

export type { AuthContextType }

interface AuthProviderProps {
	children: ReactElement<any>
	queryClient?: QueryClient
}

export type { RefreshTokenResponse } from "./useAuth"

/**
 * AuthProvider
 * @param param0
 * @returns
 */
export const AuthProvider = ({ children, queryClient }: AuthProviderProps) => {
	// State
	const [status, setStatus] = useState<AuthStateStatusEnum>(
		AuthStateStatusEnum.AUTHENTICATING,
	)

	// Context
	const { id: userId, error: errors } = useCurrentUserId()
	const { redirect } = useContext(RedirectContext)

	// If we have an error, set the status to logged out
	useEffect(() => {
		if (errors && errors.length > 0) {
			errors.forEach((error) => {
				// If we find a AUTHENTICATION_ERROR
				// If we encounter a network error, we don't want to report it
				if (
					String(error?.extensions?.type).includes(
						NetworkError.AUTHENTICATION_ERROR,
					) &&
					status !== AuthStateStatusEnum.LOGGED_OUT
				) {
					setStatus(AuthStateStatusEnum.LOGGED_OUT)
				}
			})
		}

		// When we said hi succesfully, set auth state
		if (userId && status !== AuthStateStatusEnum.LOGGED_IN) {
			// Set logged out status
			setStatus(AuthStateStatusEnum.LOGGED_IN)
		}
	}, [userId, status, errors])

	/**
	 * Logs out the user and redirects to the login page
	 * TODO: Call the backend to log out the user as well
	 */
	const logOut = useCallback(async () => {
		// If not already logging out
		if (status !== AuthStateStatusEnum.LOGGED_OUT) {
			// Clear persisted redux state
			void persistor.purge()

			/**
			 * Clear the react-query cache when a user logs out
			 *
			 * if a user logs out, and we  do not clear the cache, when they
			 * log back in, react-query will hold onto the cache of the old
			 * queries with the old user. so you'll get the previous data,
			 * which might be wrong, until the new one refetches.
			 * so this clears the react query cache when a user logs out
			 *
			 * TODO: Review that this is indeed working as expected
			 */
			queryClient?.clear()

			// Set status TODO: temp disabled for testing
			// setStatus(AuthStateStatusEnum.LOGGED_OUT)

			/**
			 * Redirect to logout page
			 *
			 * TODO: This is a temporary hack to redirect to the old logout page
			 */
			redirect(AUTH_LOGOUT_URL)
		}
	}, [status, queryClient, redirect])

	const isLoggedIn = useCallback(() => {
		return status === AuthStateStatusEnum.LOGGED_IN
	}, [status])

	return (
		<AuthContext.Provider
			value={{
				status,
				logOut,
				isLoggedIn,
			}}
		>
			{useMemo(() => children, [children])}
		</AuthContext.Provider>
	)
}

export const AuthInactivityIndicator = () => {
	const { logOut } = useContext(AuthContext)
	const t = useTrans("common")
	const { addToast } = useToasts()

	const onInactivity = useCallback(() => {
		void logOut()
		addToast({
			id: "common:auth.inactivity_message",
			text: t("common:auth.inactivity_message"),
			timing: 10000,
		})
	}, [logOut, t, addToast])

	// 30 minutes inactivity
	useOnInactivity(onInactivity)

	return null
}
